import * as React from 'react'
import { CreateProduct } from '../ProductStore'
import { uniq } from 'lodash'
import { inject, observer } from 'mobx-react'
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap'
import { API, IPricingGroup } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { FormError } from '../../../../../components/FormError'

import { parseErrorMsg } from '../../../../../utilities/helpers'
import { ReactSingleSelect, ISelectOption } from '../../../../../components/ReactSelect'

interface IProps {
  currentCompanyStore?: CurrentCompanyStore
  product: CreateProduct
  pricingGroups: IPricingGroup[]
  onClose: () => void
  onSave: () => void
}

interface IState {
  errorMessage: string
  allowPricingGroupMigration: boolean
  originalPricingGroup?: IPricingGroup
  selectedPricingGroup?: IPricingGroup
}

@inject('currentCompanyStore')
@observer
export class PricingGroupMigrationModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      errorMessage: '',
      allowPricingGroupMigration: false,
    }
  }

  async componentDidMount() {
    const { product } = this.props

    try {
      await this.setAllowPricingGroupMigration()
    } catch (error) {
      this.setState({
        errorMessage: parseErrorMsg(error) || 'There was an error getting the pricing groups',
      })
    }

    product.childProducts.forEach((childProduct) => {
      childProduct.setPricingGroupId(null)
    })
  }

  async setAllowPricingGroupMigration() {
    const company = this.props.currentCompanyStore!.company!
    const { product } = this.props

    if (product.pricingGroupId) {
      if (product.originalPricingGroupId) {
        const originalPricingGroup = await API.getPricingGroupById(
          company.id,
          product.originalPricingGroupId,
        )
        this.setState({ originalPricingGroup })
      }
      const selectedPricingGroup = await API.getPricingGroupById(company.id, product.pricingGroupId)
      this.setState({ selectedPricingGroup })
      if (product.childProducts.length <= selectedPricingGroup.childProducts.length) {
        this.setState({ allowPricingGroupMigration: true })
      }
    }
  }

  onSave = () => {
    const variantPricingGroupIds = this.props.product.childProducts.map(
      (childProduct) => childProduct.pricingGroupId,
    )
    const hasDuplicatePricingGroupId =
      uniq(variantPricingGroupIds).length !== variantPricingGroupIds.length

    if (hasDuplicatePricingGroupId) {
      return this.setState({
        errorMessage: 'You have selected the same pricing group multiple times',
      })
    }

    this.props.onSave()
  }

  render() {
    const { allowPricingGroupMigration, selectedPricingGroup, originalPricingGroup } = this.state
    const { onClose, product, pricingGroups } = this.props
    const childProducts = product.childProducts
    let matchingPricingGroup: IPricingGroup | null = null
    if (product.pricingGroupId) {
      matchingPricingGroup =
        pricingGroups.find((pricingGroup) => pricingGroup.id === product.pricingGroupId) || null
    }

    const pricingGroupVariants = matchingPricingGroup ? matchingPricingGroup.childProducts : []
    // checks if all child products have been mapped to a new pricing group variant
    const childProductsPricingGroups = childProducts.filter(
      (child) => child.pricingGroupId !== null,
    )
    const isDisabled = !(childProductsPricingGroups.length === childProducts.length)

    return (
      <Modal isOpen={true} size='md'>
        <ModalHeader toggle={onClose}>Migrate pricing groups</ModalHeader>
        <ModalBody>
          {allowPricingGroupMigration && (
            <>
              <p>Please assign pricing group to your variants. </p>
              <p>
                <b className='text-danger'>
                  Not selecting the matching pricing group will result in variant deletion
                </b>
              </p>
            </>
          )}

          {!allowPricingGroupMigration && selectedPricingGroup && originalPricingGroup && (
            <div>
              <p>
                You are trying to migrate <b>{originalPricingGroup.name}</b> with{' '}
                <b>{product.childProducts.length}</b> variants to <b>{selectedPricingGroup.name}</b>{' '}
                with <b>{selectedPricingGroup.childProducts.length}</b> variants. If you want to
                change your pricing group, remove the variants that you don’t expect to have before
                reassigning.
              </p>

              <Button className='btn btn-lg btn-light w-100' onClick={() => this.props.onClose()}>
                I understand
              </Button>
            </div>
          )}

          {allowPricingGroupMigration &&
            childProducts.map((childProduct, index) => {
              return (
                <div className='row mb-2' key={index}>
                  <div className='col-md-6'>
                    <div>Existing variant: {childProduct.name}</div>
                    <small className='text-muted'>ID: {childProduct.id}</small>
                  </div>
                  <div className='col-md-6'>
                    <ReactSingleSelect
                      placeholder='Select a variant'
                      options={pricingGroupVariants.map((pricingGroup) => {
                        return {
                          value: pricingGroup.id,
                          label: pricingGroup.name,
                        }
                      })}
                      onChange={(option: ISelectOption | null) => {
                        if (option) {
                          childProduct.setPricingGroupId(option.value)
                        } else {
                          childProduct.setPricingGroupId(null)
                        }
                      }}
                      isClearable
                    />
                  </div>
                </div>
              )
            })}

          {allowPricingGroupMigration && (
            <button
              className='btn btn-lg btn-success mt-4 w-100'
              onClick={this.onSave}
              disabled={isDisabled}
            >
              Save
            </button>
          )}

          <div className='mt-3'>
            <FormError message={this.state.errorMessage} />
          </div>
        </ModalBody>
      </Modal>
    )
  }
}
